import React, { useState } from "react";
import Icon from "@mdi/react";
import {
  mdiWarehouse,
  mdiMapMarker,
  mdiCalendarMonth,
  mdiPackageVariant,
  mdiClose,
  mdiInformationOutline,
  mdiScriptTextPlayOutline,
  mdiTruckDeliveryOutline,
} from "@mdi/js";
import { FileText, ImageIcon, Download } from "lucide-react";
import {
  ReturnableSTOutward,
  ReturnableDestinationType,
  WarehouseOutwardStatus,
} from "../../model/warehouse_outward_model";
import DeviceIdsViewer from "../../common-components/DeviceIdsViewer";
import SummaryItem from "./SummaryItem";
import ConvertToSOPriceModal from "./ConvertToSOPriceModal";
import { ConvertSTToSO } from "../../warehouse_module_repo";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { useHistory } from "react-router-dom";

const getIcon = (type: string) => {
  if (type.includes("pdf")) return <FileText size={16} />;
  if (type.includes("image")) return <ImageIcon size={16} />;
  return <FileText size={16} />;
};

type Props = {
  open: boolean;
  onClose: () => void;
  outward: ReturnableSTOutward;
};

const formatAddress = (addr?: any) => {
  if (!addr) return "-";
  return `${addr.line1}, ${addr.city}, ${addr.state} - ${addr.pincode}`;
};

const ReturnableSTView: React.FC<Props> = ({ open, onClose, outward }) => {
  const [showConvertModal, setShowConvertModal] = useState(false);
  const { showToast } = useToast();
  const history = useHistory();
  if (!open) return null;

  const {
    id: stoNo,
    status,
    from_warehouse_name: fromWarehouse,
    destination_type,
    converted_order_id,
    vendor_name,
    vendor_warehouse_address,
    customer_name,
    billing_address,
    shipping_address,
    expected_dispatch_date,
    expected_return_date,
    purpose,
    description,
    items = [],
  } = outward;

  // Cancelled STs must not expose finance details or any documents.
  const isCancelled = status === "Cancelled" || status === "Rejected";
  // Returned STs shouldn't show finance docs either, same as Cancelled.
  const hideFinanceDocs = isCancelled || status === "Returned";
  const dc_attachment = hideFinanceDocs ? undefined : outward.dc_attachment;
  const dc_ambill_attachment = hideFinanceDocs
    ? undefined
    : outward.dc_ambill_attachment;
  const eway_attachment = hideFinanceDocs ? undefined : outward.eway_attachment;
  const invoice_attachment = hideFinanceDocs
    ? undefined
    : outward.invoice_attachment;

  const allDeviceIds: string[] =
    items.reduce((acc: string[], item) => {
      if (
        item.subCategory === "Finished Goods" &&
        Array.isArray(item.deviceIds)
      ) {
        acc.push(...item.deviceIds);
      }
      return acc;
    }, []) ?? [];

  const totalValue =
    items.reduce((sum, i) => sum + (i.itemPrice || 0) * i.requestedQty, 0) || 0;

  return (
    <div
      id="warehouse-stock-transfer-returnable-view-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4 sm:p-8"
    >
      <div className="relative w-full sm:max-w-2xl max-h-[85vh] bg-white shadow-lg flex flex-col rounded-lg wh-modal-xs">
        {/* Header */}
        <div className="flex-shrink-0 flex items-start justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">{stoNo}</h2>

            <span className="rounded-full border px-2.5 py-0.5 text-xs font-medium">
              Returnable ST
            </span>

            <span className="rounded-full bg-blue-100 text-blue-700 px-2.5 py-0.5 text-xs font-medium">
              {status}
            </span>
          </div>

          <button
            id="warehouse-stock-transfer-returnable-view-modal-close-btn"
            onClick={onClose}
            className="rounded-md p-1 hover:bg-gray-100"
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <SummaryItem
              icon={mdiWarehouse}
              label="From Warehouse"
              value={fromWarehouse}
              color="text-blue-600"
            />

            {/* Destination block */}
            {destination_type === ReturnableDestinationType.VENDOR ? (
              <>
                <SummaryItem
                  icon={mdiMapMarker}
                  label="Vendor"
                  value={vendor_name}
                  color="text-green-600"
                />
                <SummaryItem
                  icon={mdiMapMarker}
                  label="Vendor Address"
                  value={formatAddress(vendor_warehouse_address)}
                  color="text-green-600"
                />
              </>
            ) : (
              <>
                <SummaryItem
                  icon={mdiMapMarker}
                  label="Customer"
                  value={customer_name}
                  color="text-green-600"
                />
                <SummaryItem
                  icon={mdiMapMarker}
                  label="Billing Address"
                  value={formatAddress(billing_address)}
                  color="text-green-600"
                />
                <SummaryItem
                  icon={mdiMapMarker}
                  label="Shipping Address"
                  value={formatAddress(shipping_address)}
                  color="text-green-600"
                />
              </>
            )}

            <SummaryItem
              icon={mdiCalendarMonth}
              label="Dispatch Date"
              value={
                expected_dispatch_date
                  ? new Date(expected_dispatch_date).toISOString().split("T")[0]
                  : "-"
              }
              color="text-purple-600"
            />

            <SummaryItem
              icon={mdiCalendarMonth}
              label="Return Date"
              value={
                expected_return_date
                  ? new Date(expected_return_date).toISOString().split("T")[0]
                  : "-"
              }
              color="text-purple-600"
            />

            {!isCancelled && (
              <SummaryItem
                icon={mdiPackageVariant}
                label="Total Value"
                value={`₹${totalValue.toLocaleString()}`}
                color="text-orange-600"
              />
            )}

            {converted_order_id && (
              <SummaryItem
                icon={mdiInformationOutline}
                label="Order ID"
                value={converted_order_id}
                color="text-gray-600"
              />
            )}

            <SummaryItem
              icon={mdiInformationOutline}
              label="Purpose"
              value={purpose}
              color="text-gray-600"
            />
            <SummaryItem
              icon={mdiScriptTextPlayOutline}
              label="Description"
              value={description}
              color="text-gray-500"
            />
            {outward.shipping_details && (
              <>
                <SummaryItem
                  icon={mdiTruckDeliveryOutline}
                  label="Logistic Partner"
                  value={outward.shipping_details.logistic_partner}
                  color="text-indigo-600"
                />
                <SummaryItem
                  icon={mdiTruckDeliveryOutline}
                  label="Tracking ID"
                  value={outward.shipping_details.tracking_id}
                  color="text-indigo-600"
                />
              </>
            )}
          </div>

          {/* Attachment */}
          {dc_attachment && (
            <div className="border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  {getIcon(dc_attachment.type)}
                  <span className="truncate">{dc_attachment.filename}</span>
                </div>

                <a
                  id="warehouse-stock-transfer-returnable-view-modal-dc-attachment-link"
                  href={dc_attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                >
                  <Download size={14} />
                  View
                </a>
              </div>
            </div>
          )}
          {eway_attachment && (
            <div className="border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  {getIcon(eway_attachment.type)}
                  <span className="truncate">{eway_attachment.filename}</span>
                </div>

                <a
                  id="warehouse-stock-transfer-returnable-view-modal-eway-attachment-link"
                  href={eway_attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                >
                  <Download size={14} />
                  View
                </a>
              </div>
            </div>
          )}

          {invoice_attachment && (
            <div className="border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  {getIcon(invoice_attachment.type)}
                  <span className="truncate">
                    {invoice_attachment.filename}
                  </span>
                </div>

                <a
                  id="warehouse-stock-transfer-returnable-view-modal-invoice-attachment-link"
                  href={invoice_attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                >
                  <Download size={14} />
                  View
                </a>
              </div>
            </div>
          )}

          {dc_ambill_attachment && (
            <div className="border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  {getIcon(dc_ambill_attachment.type)}
                  <span className="truncate">
                    {dc_ambill_attachment.filename}
                  </span>
                </div>

                <a
                  id="warehouse-stock-transfer-returnable-view-modal-dc-ambill-attachment-link"
                  href={dc_ambill_attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                >
                  <Download size={14} />
                  View
                </a>
              </div>
            </div>
          )}

          {/* Grouped Attachments */}
          {!isCancelled &&
            outward.attachments &&
            outward.attachments.length > 0 && (
              <div className="space-y-4">
                {Object.entries(
                  outward.attachments.reduce(
                    (acc, file) => {
                      const category = file.category || "Other Attachments";
                      if (!acc[category]) acc[category] = [];
                      acc[category].push(file);
                      return acc;
                    },
                    {} as Record<string, typeof outward.attachments>,
                  ),
                ).map(([category, files]) => (
                  <div key={category} className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <FileText size={16} className="text-blue-600" />
                      {category}
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {files.map((file, idx) => (
                        <div
                          key={file.id || idx}
                          id={`warehouse-stock-transfer-returnable-view-modal-other-attachment-${file.id ?? idx}`}
                          className="flex items-center justify-between border rounded-lg bg-gray-50 px-3 py-2 text-sm"
                        >
                          <div className="flex items-center gap-2 truncate">
                            {getIcon(file.type)}
                            <span className="truncate">
                              {file?.name ?? `Attachment ${idx + 1}`}
                            </span>
                          </div>

                          <a
                            id={`warehouse-stock-transfer-returnable-view-modal-other-attachment-${file.id ?? idx}-view-link`}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                          >
                            <Download size={14} />
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

          {/* Released Devices */}
          {(status === "Rejected" || status === "Cancelled") &&
            allDeviceIds.length > 0 && (
              <DeviceIdsViewer
                deviceIds={allDeviceIds}
                title="Released Devices"
              />
            )}

          {/* Items */}
          <div className="space-y-4">
            {items.map((item, idx) => (
              <div
                key={idx}
                id={`warehouse-stock-transfer-returnable-view-modal-item-row-${item.inventoryItemCode ?? idx}`}
                className="rounded-lg border p-4 space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{item.itemName}</p>
                    <p className="text-sm text-gray-500">
                      {item.inventoryItemCode}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Qty: {item.requestedQty}
                    </p>
                    {!isCancelled && (
                      <p className="text-xs text-gray-600 mt-1">
                        Unit Price: ₹{item.itemPrice?.toLocaleString()}
                      </p>
                    )}
                  </div>
                  {!isCancelled && (
                    <p className="font-medium">
                      ₹{item.requestedQty * (item.itemPrice || 0)}
                    </p>
                  )}
                </div>

                {status !== "Rejected" &&
                  status !== "Cancelled" &&
                  item.subCategory === "Finished Goods" && (
                    <DeviceIdsViewer
                      deviceIds={item.deviceIds || []}
                      title="Allocated Devices"
                    />
                  )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        {destination_type === ReturnableDestinationType.CUSTOMER &&
          !converted_order_id &&
          status === WarehouseOutwardStatus.DISPATCHED && (
            <div className="border-t px-6 py-4 flex justify-end">
              <button
                id="warehouse-stock-transfer-returnable-view-modal-convert-to-so-btn"
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700"
                onClick={() => setShowConvertModal(true)}
              >
                Convert to SO
              </button>
            </div>
          )}
      </div>
      <ConvertToSOPriceModal
        open={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        items={items}
        onCreate={async (prices) => {
          const res = await ConvertSTToSO(outward.id, prices);

          if (res?.success) {
            setShowConvertModal(false);
            showToast({
              type: ToastType.success,
              text: res.data?.message || "ST converted to SO successfully",
            });
            history.goBack();
            return;
          } else {
            // TODO: toast error
            showToast({
              type: ToastType.error,
              text: res?.error || "Failed to convert ST to SO",
            });
            console.error(res?.error);
          }
        }}
      />
      ;
    </div>
  );
};

export default ReturnableSTView;
